<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="handleCancel">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Nouveau retrait de stock</h1>
          <p class="text-xs text-gray-400">Enregistrez un retrait et ajustez votre inventaire</p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="handleCancel">
          Annuler
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :disabled="isSubmitting || items.length === 0"
          @click="handleSubmit">
          <span v-if="isSubmitting" class="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
          <template v-else>
            <i class="fas fa-check text-[10px]"></i>
            Enregistrer
          </template>
        </button>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Informations du retrait -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <i class="fas fa-info-circle text-primary text-xs"></i>
            <h2 class="text-sm font-semibold text-gray-700">Informations du retrait</h2>
          </div>
          <div class="p-5">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Date -->
              <div>
                <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">
                  Date de retrait <span class="text-red-400">*</span>
                </label>
                <input type="date" v-model="form.withdrawal_date"
                  class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                  :class="{ '!border-red-300 !bg-red-50/50': errors.withdrawal_date }" />
                <p v-if="errors.withdrawal_date" class="text-[11px] text-red-500 mt-1">{{ errors.withdrawal_date }}</p>
              </div>

              <!-- Raison -->
              <div>
                <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">
                  Raison du retrait <span class="text-red-400">*</span>
                </label>
                <select v-model="form.reason"
                  class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none"
                  :class="{ '!border-red-300 !bg-red-50/50': errors.reason }">
                  <option value="">Sélectionnez une raison</option>
                  <option value="damage">Dommage/Casse</option>
                  <option value="loss">Perte</option>
                  <option value="theft">Vol</option>
                  <option value="expired">Expiré</option>
                  <option value="return_to_supplier">Retour fournisseur</option>
                  <option value="adjustment">Ajustement</option>
                  <option value="other">Autre</option>
                </select>
                <p v-if="errors.reason" class="text-[11px] text-red-500 mt-1">{{ errors.reason }}</p>
              </div>

              <!-- Notes -->
              <div class="md:col-span-2">
                <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">Remarques</label>
                <textarea v-model="form.notes" rows="2" placeholder="Notes additionnelles..."
                  class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-y min-h-[48px]"></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Articles à retirer -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <i class="fas fa-box text-primary text-xs"></i>
              <h2 class="text-sm font-semibold text-gray-700">Articles à retirer</h2>
              <span class="text-[10px] text-gray-400 font-medium">({{ items.length }})</span>
            </div>
          </div>

          <!-- Barre de recherche rapide -->
          <div class="px-5 py-3 border-b border-gray-100 flex gap-2">
            <div class="flex-1 relative">
              <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
              <input type="text" v-model="globalSearch"
                placeholder="Rechercher un produit (avec suivi de stock)..."
                class="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                @input="onGlobalSearch" />
              <span v-if="globalSuggestions.length > 0"
                class="absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/10 text-primary">
                {{ globalSuggestions.length }} trouvé{{ globalSuggestions.length > 1 ? 's' : '' }}
              </span>
            </div>
            <button
              class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors"
              :disabled="!canAddFromGlobal"
              @click="addLineFromGlobal">
              <i class="fas fa-plus text-[10px]"></i>
              Ajouter
            </button>
          </div>

          <!-- État vide -->
          <div v-if="items.length === 0" class="flex flex-col items-center justify-center py-14 text-center px-6">
            <div class="text-gray-300 mb-3">
              <i class="fas fa-box-open text-5xl"></i>
            </div>
            <h3 class="text-base font-semibold text-gray-700 mb-1">Aucun article ajouté</h3>
            <p class="text-sm text-gray-400 max-w-sm mb-5">Recherchez et ajoutez des produits à retirer du stock</p>
            <button
              class="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white text-xs font-medium rounded-lg transition-colors"
              @click="addEmptyLine">
              <i class="fas fa-plus text-[10px]"></i>
              Ajouter le premier article
            </button>
          </div>

          <!-- Tableau -->
          <div v-else>
            <div>
              <table class="w-full">
                <thead>
                  <tr class="bg-gray-50/80 border-b border-gray-200">
                    <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[220px]">Produit</th>
                    <th class="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Stock</th>
                    <th class="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">Qté à retirer</th>
                    <th class="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Après</th>
                    <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Notes</th>
                    <th class="px-5 py-3 w-12"></th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  <tr v-for="(line, index) in items" :key="index"
                    class="transition-colors"
                    :class="line.hasError ? 'bg-red-50/50' : 'hover:bg-gray-50/80'">
                    <!-- Produit -->
                    <td class="px-5 py-3">
                      <div class="relative" :ref="el => setLineRef(el, index)">
                        <div class="relative">
                          <i class="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 text-[10px]"></i>
                          <input type="text" v-model="line.search"
                            :placeholder="line.product ? line.product.name : 'Rechercher...'"
                            @input="searchProducts(index)"
                            @focus="line.showDropdown = true"
                            class="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                            :class="{ '!border-red-300': line.error }" />
                        </div>
                        <Transition name="dropdown">
                          <div v-if="line.showDropdown && line.suggestions.length"
                            class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[180px] overflow-y-auto z-50">
                            <div v-for="p in line.suggestions" :key="p.id" @click="selectProduct(index, p)"
                              class="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0">
                              <div>
                                <p class="font-medium text-gray-900 text-sm">{{ p.name }}</p>
                                <p class="text-[11px] text-gray-400">#{{ p.id }}</p>
                              </div>
                              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                                :class="p.stock_quantity > 5 ? 'bg-emerald-50 text-emerald-700' : p.stock_quantity > 0 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'">
                                Stock: {{ p.stock_quantity || 0 }}
                              </span>
                            </div>
                          </div>
                        </Transition>
                        <p v-if="line.error" class="text-[10px] text-red-500 mt-0.5">{{ line.error }}</p>
                      </div>
                    </td>

                    <!-- Stock disponible -->
                    <td class="px-5 py-3 text-center">
                      <span v-if="line.product" class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        :class="getStockBadgeClass(line.product.stock_quantity)">
                        {{ line.product.stock_quantity || 0 }}
                      </span>
                      <span v-else class="text-xs text-gray-300">-</span>
                    </td>

                    <!-- Quantité à retirer -->
                    <td class="px-5 py-3">
                      <div class="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden mx-auto w-fit"
                        :class="{ '!border-red-300': line.quantityError }">
                        <button type="button" @click="adjustQuantity(index, -1)"
                          class="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                          <i class="fas fa-minus text-[10px]"></i>
                        </button>
                        <input v-model.number="line.quantity" type="number" min="1" step="1"
                          @input="validateQuantity(index)"
                          class="w-12 h-8 text-center text-sm font-semibold text-gray-900 border-x border-gray-200 bg-transparent outline-none" />
                        <button type="button" @click="adjustQuantity(index, 1)"
                          class="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-primary hover:bg-primary/5 transition-colors">
                          <i class="fas fa-plus text-[10px]"></i>
                        </button>
                      </div>
                      <p v-if="line.quantityError" class="text-[10px] text-red-500 mt-0.5 text-center">{{ line.quantityError }}</p>
                    </td>

                    <!-- Stock après -->
                    <td class="px-5 py-3 text-center">
                      <span v-if="line.product && line.quantity" class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        :class="getStockBadgeClass(calculateStockAfter(line))">
                        {{ calculateStockAfter(line) }}
                      </span>
                      <span v-else class="text-xs text-gray-300">-</span>
                    </td>

                    <!-- Notes -->
                    <td class="px-5 py-3">
                      <input type="text" v-model="line.notes" placeholder="Optionnel..."
                        class="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" />
                    </td>

                    <!-- Supprimer -->
                    <td class="px-5 py-3">
                      <button @click="removeLine(index)"
                        class="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                        <i class="fas fa-trash text-xs"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Ajouter une ligne -->
            <div class="px-5 py-3 border-t border-dashed border-gray-200">
              <button @click="addEmptyLine"
                class="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary-dark transition-colors">
                <i class="fas fa-plus text-[10px]"></i>
                Ajouter une ligne
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Résumé -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-5">
          <div class="px-5 py-3 border-b bg-primary/5 border-primary/10">
            <h3 class="text-sm font-semibold text-primary">Résumé</h3>
          </div>
          <div class="p-5 space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-box text-gray-400 text-[10px]"></i>
                </span>
                Articles
              </span>
              <span class="text-sm font-semibold text-gray-900">{{ items.length }}</span>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-cubes text-blue-500 text-[10px]"></i>
                </span>
                Quantité totale
              </span>
              <span class="text-sm font-bold text-primary">{{ totalQuantity }} unité(s)</span>
            </div>
          </div>
          <div class="px-5 pb-5">
            <button
              class="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              :disabled="isSubmitting || items.length === 0"
              @click="handleSubmit">
              <span v-if="isSubmitting" class="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              <template v-else>
                <i class="fas fa-check text-[10px]"></i>
                Enregistrer le retrait
              </template>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Annulation -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showCancelModal"
          class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          @click.self="showCancelModal = false">
          <div class="bg-white rounded-xl w-full max-w-sm p-6 text-center shadow-2xl">
            <div class="w-14 h-14 rounded-full bg-amber-50 mx-auto mb-4 flex items-center justify-center">
              <i class="fas fa-triangle-exclamation text-amber-500 text-xl"></i>
            </div>
            <h3 class="text-base font-bold text-gray-900 mb-2">Annuler le retrait ?</h3>
            <p class="text-sm text-gray-500 mb-6 leading-relaxed">
              Toutes les informations saisies seront perdues.
            </p>
            <div class="flex gap-3">
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                @click="showCancelModal = false">
                Continuer
              </button>
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                @click="confirmCancel">
                Oui, annuler
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Modal Confirmation soumission -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showSubmitModal"
          class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          @click.self="showSubmitModal = false">
          <div class="bg-white rounded-xl w-full max-w-sm p-6 text-center shadow-2xl">
            <div class="w-14 h-14 rounded-full bg-emerald-50 mx-auto mb-4 flex items-center justify-center">
              <i class="fas fa-check text-emerald-500 text-xl"></i>
            </div>
            <h3 class="text-base font-bold text-gray-900 mb-4">Confirmer le retrait ?</h3>
            <div class="bg-gray-50 rounded-lg p-3 mb-6 text-left space-y-2">
              <div class="flex justify-between text-sm">
                <span class="text-gray-400">Date</span>
                <span class="font-medium text-gray-900">{{ formatDate(form.withdrawal_date) }}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-400">Raison</span>
                <span class="font-medium text-gray-900">{{ getReasonLabel(form.reason) }}</span>
              </div>
              <div class="h-px bg-gray-200"></div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-400">Articles</span>
                <span class="font-semibold text-gray-900">{{ items.length }}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-400">Quantité totale</span>
                <span class="font-bold text-primary">{{ totalQuantity }} unité(s)</span>
              </div>
            </div>
            <div class="flex gap-3">
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                @click="showSubmitModal = false">
                Annuler
              </button>
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50"
                :disabled="isSubmitting"
                @click="confirmSubmit">
                <span v-if="isSubmitting" class="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block mr-1"></span>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import { productsService } from '@/services'
import stockWithdrawalService from '@/services/stockWithdrawalService'
import { useToastStore } from '@/store/modules/toastStore'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'CreateStockWithdrawalView',
  setup() {
    const router = useRouter()
    const toastStore = useToastStore()

    const isSubmitting = ref(false)
    const errors = ref({})
    const form = ref({
      withdrawal_date: new Date().toISOString().slice(0, 10),
      reason: '',
      notes: ''
    })

    const items = ref([])
    const globalSearch = ref('')
    const globalSuggestions = ref([])
    const globalSearchTimer = ref(null)
    const lineRefs = ref({})

    const showCancelModal = ref(false)
    const showSubmitModal = ref(false)

    const canAddFromGlobal = computed(() => globalSuggestions.value.length > 0)

    const totalQuantity = computed(() => {
      return items.value.reduce((sum, line) => {
        return sum + (Number(line.quantity) || 0)
      }, 0)
    })

    const setLineRef = (el, index) => {
      if (el) lineRefs.value[index] = el
    }

    const addEmptyLine = () => {
      items.value.push({
        product: null,
        product_id: null,
        search: '',
        suggestions: [],
        showDropdown: false,
        quantity: 1,
        notes: '',
        error: null,
        quantityError: null,
        hasError: false
      })
    }

    const removeLine = (index) => {
      items.value.splice(index, 1)
    }

    const searchProducts = (index) => {
      const line = items.value[index]
      if (!line) return
      const q = (line.search || '').trim()
      if (!q) {
        line.suggestions = []
        return
      }
      productsService.getProducts({
        search: q,
        per_page: 10,
        track_inventory: true
      }).then(res => {
        const data = res.data
        const allProducts = Array.isArray(data) ? data : (data.data || [])
        line.suggestions = allProducts.filter(p => p.track_inventory === true)
      }).catch(() => {
        line.suggestions = []
      }).finally(() => {
        line.showDropdown = true
      })
    }

    const selectProduct = (index, product) => {
      const line = items.value[index]
      if (!line) return

      if (!product.track_inventory) {
        toastStore.addToast({ type: 'error', message: 'Ce produit ne suit pas l\'inventaire', duration: 3000 })
        return
      }

      const duplicateIndex = items.value.findIndex((l, i) => l.product_id === product.id && i !== index)
      if (duplicateIndex !== -1) {
        toastStore.addToast({ type: 'warning', message: 'Ce produit est déjà ajouté', duration: 3000 })
        line.showDropdown = false
        line.suggestions = []
        line.search = ''
        return
      }

      line.product = product
      line.product_id = product.id
      line.search = product.name
      line.showDropdown = false
      line.suggestions = []
      line.quantity = 1
      line.error = null
      line.hasError = false
      validateQuantity(index)
      addEmptyLine()
    }

    const validateQuantity = (index) => {
      const line = items.value[index]
      if (!line) return false

      line.quantityError = null
      line.hasError = false

      if (!line.product || !line.product_id) {
        line.error = 'Sélectionnez un produit'
        line.hasError = true
        return false
      }

      const quantity = Number(line.quantity) || 0
      const availableStock = Number(line.product.stock_quantity) || 0

      if (quantity <= 0) {
        line.quantityError = 'Min. 1'
        line.hasError = true
        return false
      }

      if (quantity > availableStock) {
        line.quantityError = `Max: ${availableStock}`
        line.hasError = true
        return false
      }

      line.error = null
      line.hasError = false
      return true
    }

    const adjustQuantity = (index, delta) => {
      const line = items.value[index]
      if (!line) return
      const current = Number(line.quantity) || 0
      line.quantity = Math.max(1, current + delta)
      validateQuantity(index)
    }

    const calculateStockAfter = (line) => {
      if (!line.product || !line.quantity) return 0
      const currentStock = Number(line.product.stock_quantity) || 0
      const quantity = Number(line.quantity) || 0
      return Math.max(0, currentStock - quantity)
    }

    const getStockBadgeClass = (stock) => {
      if (stock <= 0) return 'bg-red-50 text-red-600'
      if (stock <= 5) return 'bg-amber-50 text-amber-700'
      return 'bg-emerald-50 text-emerald-700'
    }

    const onGlobalSearch = () => {
      if (globalSearchTimer.value) clearTimeout(globalSearchTimer.value)
      const q = (globalSearch.value || '').trim()
      if (!q) {
        globalSuggestions.value = []
        return
      }
      globalSearchTimer.value = setTimeout(() => {
        productsService.getProducts({ search: q, per_page: 10, track_inventory: true })
          .then(res => {
            const data = res.data
            const allProducts = Array.isArray(data) ? data : (data.data || [])
            globalSuggestions.value = allProducts.filter(p => p.track_inventory === true)
          })
          .catch(() => { globalSuggestions.value = [] })
      }, 300)
    }

    const addLineFromGlobal = () => {
      if (!globalSuggestions.value.length) return
      const product = globalSuggestions.value[0]
      const existingIndex = items.value.findIndex(l => l.product_id === product.id)
      if (existingIndex !== -1) {
        toastStore.addToast({ type: 'warning', message: 'Ce produit est déjà présent', duration: 3000 })
        return
      }
      addEmptyLine()
      selectProduct(items.value.length - 1, product)
      globalSearch.value = ''
      globalSuggestions.value = []
    }

    const validate = () => {
      const e = {}
      if (!form.value.withdrawal_date) e.withdrawal_date = 'La date est requise'
      if (!form.value.reason) e.reason = 'La raison est requise'
      if (items.value.length === 0) {
        toastStore.addToast({ type: 'error', message: 'Ajoutez au moins un article', duration: 3000 })
        return false
      }

      let hasErrors = false
      for (let i = 0; i < items.value.length; i++) {
        if (!validateQuantity(i)) hasErrors = true
      }

      if (hasErrors) {
        toastStore.addToast({ type: 'error', message: 'Vérifiez les quantités', duration: 3500 })
        return false
      }

      const productIds = items.value.map(l => l.product_id).filter(Boolean)
      const hasDuplicates = new Set(productIds).size !== productIds.length
      if (hasDuplicates) {
        toastStore.addToast({ type: 'error', message: 'Doublons de produits détectés', duration: 3500 })
        return false
      }

      errors.value = e
      return Object.keys(e).length === 0
    }

    const handleCancel = () => {
      if (items.value.some(l => l.product_id) || form.value.notes) {
        showCancelModal.value = true
      } else {
        router.push({ name: 'stock-withdrawals' })
      }
    }

    const confirmCancel = () => {
      showCancelModal.value = false
      router.push({ name: 'stock-withdrawals' })
    }

    const handleSubmit = () => {
      if (!validate()) return
      showSubmitModal.value = true
    }

    const confirmSubmit = async () => {
      if (isSubmitting.value) return
      isSubmitting.value = true
      try {
        const payload = {
          withdrawal_date: form.value.withdrawal_date,
          reason: form.value.reason,
          notes: form.value.notes || null,
          items: items.value
            .filter(l => l.product_id && l.quantity > 0)
            .map(l => ({
              product_id: l.product_id,
              quantity: Number(l.quantity),
              notes: l.notes || null
            }))
        }

        await stockWithdrawalService.createStockWithdrawal(payload)
        toastStore.addToast({ type: 'success', message: 'Retrait créé avec succès', duration: 4000 })
        showSubmitModal.value = false
        router.push({ name: 'stock-withdrawals' })
      } catch (err) {
        console.error('Erreur création retrait:', err)
        toastStore.addToast({
          type: 'error',
          message: err?.response?.data?.message || 'Erreur lors de la création',
          duration: 4000
        })
      } finally {
        isSubmitting.value = false
      }
    }

    const formatDate = (date) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric', month: 'long', day: 'numeric'
      })
    }

    const getReasonLabel = (reason) => {
      return stockWithdrawalService.getReasonLabel(reason) || reason
    }

    addEmptyLine()

    return {
      form, errors, isSubmitting, items,
      globalSearch, globalSuggestions, canAddFromGlobal,
      addLineFromGlobal, onGlobalSearch, addEmptyLine, removeLine,
      searchProducts, selectProduct, validateQuantity, adjustQuantity,
      calculateStockAfter, getStockBadgeClass, totalQuantity,
      handleCancel, confirmCancel, handleSubmit, confirmSubmit,
      formatDate, getReasonLabel, showCancelModal, showSubmitModal,
      setLineRef
    }
  }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.95) translateY(-8px);
}
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
